import { CrownOutlined, RiseOutlined, TeamOutlined, WarningOutlined } from '@ant-design/icons';
import { Card, Col, Progress, Row, Statistic, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { DashboardStats } from '@/types';

const { Text } = Typography;

interface SubscriptionOverviewProps {
	stats: DashboardStats;
	loading?: boolean;
}

const SubscriptionOverview = ({ stats, loading = false }: SubscriptionOverviewProps) => {
	const { t } = useTranslation('components');
	const totalOwners = Math.max(stats.totalUsers, 1);
	const proPercent = Math.min(100, Math.round((stats.activeSubscriptions / totalOwners) * 100));
	const freePercent = Math.max(0, 100 - proPercent);

	return (
		<Card
			title={
				<span className='font-semibold text-slate-700'>
					{t('dashboard.subscriptionOverview.title')}
				</span>
			}
			className='border border-slate-100 shadow-sm rounded-xl'
			styles={{ body: { padding: '16px' } }}
			loading={loading}
		>
			<Row gutter={[16, 16]}>
				<Col xs={12} sm={6}>
					<Statistic
						title={t('dashboard.subscriptionOverview.proSubs')}
						value={stats.activeSubscriptions}
						prefix={<CrownOutlined className='text-amber-500' />}
						valueStyle={{ fontSize: '22px', fontWeight: 700 }}
					/>
				</Col>
				<Col xs={12} sm={6}>
					<Statistic
						title={t('dashboard.subscriptionOverview.freeUsers')}
						value={stats.freeUsers}
						prefix={<TeamOutlined className='text-slate-400' />}
						valueStyle={{ fontSize: '22px', fontWeight: 700 }}
					/>
				</Col>
				<Col xs={12} sm={6}>
					<Statistic
						title={t('dashboard.subscriptionOverview.conversion')}
						value={stats.premiumConversionRate}
						suffix='%'
						prefix={<RiseOutlined className='text-emerald-500' />}
						valueStyle={{ fontSize: '22px', fontWeight: 700 }}
					/>
				</Col>
				<Col xs={12} sm={6}>
					<Statistic
						title={t('dashboard.subscriptionOverview.newProWeek')}
						value={stats.newPremiumThisWeek}
						prefix={<CrownOutlined className='text-indigo-500' />}
						valueStyle={{ fontSize: '22px', fontWeight: 700 }}
					/>
				</Col>
			</Row>

			<div className='mt-5 space-y-3'>
				<div>
					<div className='mb-1 flex items-center justify-between'>
						<Text className='text-xs text-slate-500'>
							{t('dashboard.subscriptionOverview.proShare')}
						</Text>
						<Text className='text-xs font-medium text-slate-700'>{proPercent}%</Text>
					</div>
					<Progress
						percent={proPercent}
						showInfo={false}
						strokeColor='#f59e0b'
						trailColor='#e2e8f0'
						size={['100%', 10]}
					/>
				</div>

				<div>
					<div className='mb-1 flex items-center justify-between'>
						<Text className='text-xs text-slate-500'>
							{t('dashboard.subscriptionOverview.freeShare')}
						</Text>
						<Text className='text-xs font-medium text-slate-700'>{freePercent}%</Text>
					</div>
					<Progress
						percent={freePercent}
						showInfo={false}
						strokeColor='#94a3b8'
						trailColor='#e2e8f0'
						size={['100%', 10]}
					/>
				</div>

				{stats.gracePeriodSubscriptions > 0 && (
					<div className='flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2'>
						<WarningOutlined className='text-amber-600' />
						<Text className='text-xs text-amber-800'>
							{t('dashboard.subscriptionOverview.gracePeriod', {
								count: stats.gracePeriodSubscriptions,
							})}
						</Text>
					</div>
				)}
			</div>
		</Card>
	);
};

export { SubscriptionOverview };
export default SubscriptionOverview;
